import type { UserSegmentationData } from "@medbillai/generated-graphql-types";

// Map features to their corresponding action text
const featuresActionMap: Record<string, string> = {
  MAXIMIZE_BENEFITS: "understand insurance benefits.",
  FIX_BILLING_ERRORS: "fix billing issues.",
  FIND_DOCTOR: "find a doctor.",
  PAY_BILLS: "pay medical bills.",
  SOMETHING_ELSE: "get the help you need.",
};
/**
 * ─────────────────────────────────────────────────────────────────────────────
 * 💡 HOW TO ADD ANOTHER ON-BOARDING QUESTION
 * ─────────────────────────────────────────────────────────────────────────────
 * 1.  getStartedConfig.ts
 *    • Append a new stage object:
 *        {
 *          id:        'insurance_type',
 *          header:    'Which type of insurance do you have?',
 *          storeKey:  'insuranceTypes',       // 👈 name of the new field below
 *          options: [
 *            { id: 'PPO',   icon: '🏥', label: 'PPO' },
 *            { id: 'HMO',   icon: '🏠', label: 'HMO' },
 *            { id: 'EPO',   icon: '📋', label: 'EPO' },
 *            { id: 'OTHER', icon: '❓', label: 'Other' },
 *          ],
 *        }
 *
 * 2.  signUpOnboardingStore.ts
 *    • Extend the UserSegmentationData interface:
 *        export interface UserSegmentationData {
 *          useCases?:       string[];
 *          usageScopes?:    string[];
 *          features?:       string[];
 *          insuranceTypes?: string[];   // 👈 add this line
 *        }
 *    • No other store logic changes; the generic `storeKey` system
 *      will persist selections into `insuranceTypes` automatically.
 *
 * 3.  GraphQL
 *    • Add the field to your schema & regenerated types:
 *        type UserSegmentation {
 *          useCases:       [String!]
 *          usageScopes:    [String!]
 *          features:       [String!]
 *          insuranceTypes: [String!]    # 👈 new
 *        }
 *
 * 4.  sendMagicLink mutation (or whichever API persists data)
 *    • Spread the new field when you build the payload:
 *        const { userSegmentationData } = useSignUpOnboardingStore.getState();
 *        await sendMagicLink({
 *          variables: {
 *            …,
 *            segmentation: {
 *              ...userSegmentationData,
 *              insuranceTypes: userSegmentationData.insuranceTypes ?? [],
 *            },
 *          },
 *        });
 *
 * ────────────────────────────────────────────────────────────────────────────
 */

export const useCasesOptions: Option[] = [
  { id: "ME", icon: "🧑‍⚕️", label: "Myself" },
  { id: "PARTNER", icon: "❤️", label: "Spouse or partner" },
  { id: "CHILD", icon: "👶", label: "Children" },
  { id: "OTHER", icon: "👥", label: "Someone else" },
];

export const featureOptions: Option[] = [
  {
    id: "MAXIMIZE_BENEFITS",
    icon: "🔍",
    label: "Understand insurance benefits",
  },
  { id: "FIX_BILLING_ERRORS", icon: "🧾", label: "Fix billing issues" },
  { id: "FIND_DOCTOR", icon: "🏥", label: "Find a doctor" },
  { id: "PAY_BILLS", icon: "💰", label: "Pay medical bills" },
  { id: "SOMETHING_ELSE", icon: "❓", label: "Something else" },
];

export type Option = { id: string; icon: string; label: string };

export type Stage = {
  id: string;
  header?: string[]; // array of header lines to display
  // Function to generate header based on user data
  getHeader?: (userSegmentationData: UserSegmentationData) => string[];
  choices?: {
    // which key inside userSegmentationData this stage writes to
    storeKey: keyof UserSegmentationData;
    options: Option[];
  };
  // Auto-continue after specified delay in ms (0 or undefined = no auto-continue)
  autoContinue?: number;
};

export const stages: Stage[] = [
  {
    id: "intro",
    autoContinue: 200,
  },
  {
    id: "greeting",
    header: ["Hi there 👋 Nice to meet you"],
    autoContinue: 1200,
  },
  {
    id: "help",
    header: ["What can we help you with?"],
    choices: {
      storeKey: "features",
      options: featureOptions,
    },
  },
  {
    id: "thanks",
    getHeader: (userSegmentationData: UserSegmentationData) => {
      const selectedFeature = userSegmentationData.features?.[0];
      const actionText = selectedFeature
        ? featuresActionMap[selectedFeature] || "get the help you need."
        : "get the help you need.";

      return ["Thanks for letting us know!", `Let's help you ${actionText}.`];
    },
    autoContinue: 1200,
  },
];
